import React, { useState, useEffect, useRef } from 'react';
import {
    FaPaperPlane,
    FaUser,
    FaComments,
    FaCircle,
    FaClock,
    FaCheckDouble,
    FaSpinner
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';

const LiveChat = () => {
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);
    const [pollingInterval, setPollingInterval] = useState(null);

    // Poll for active chats
    useEffect(() => {
        loadActiveChats();
        const interval = setInterval(loadActiveChats, 5000); // 5 sec poll
        return () => clearInterval(interval);
    }, []);

    // Poll for messages when chat selected
    useEffect(() => {
        if (selectedChat) {
            loadMessages(selectedChat.sessionId);
            const interval = setInterval(() => loadMessages(selectedChat.sessionId), 3000); // 3 sec poll
            setPollingInterval(interval);
            return () => clearInterval(interval);
        }
    }, [selectedChat]);

    // Scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const loadActiveChats = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(API_ENDPOINTS.CUSTOMER_PORTAL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    action: 'get-active-chats'
                })
            });
            const data = await response.json();
            if (data.chats) {
                setChats(data.chats);
            }
        } catch (error) {
            console.error('Error loading chats:', error);
        }
    };

    const loadMessages = async (sessionId) => {
        try {
            const token = localStorage.getItem('adminToken');
            // Optimisation: could pass lastTimestamp
            const response = await fetch(API_ENDPOINTS.CUSTOMER_PORTAL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    action: 'get-chat-messages',
                    sessionId: sessionId,
                    isAdmin: true
                })
            });
            const data = await response.json();
            if (data.messages) {
                // Sort by timestamp asc
                const sorted = data.messages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                setMessages(sorted);
            }
        } catch (error) {
            console.error('Error loading messages:', error);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputText.trim() || !selectedChat) return;

        try {
            setSending(true);
            const token = localStorage.getItem('adminToken');
            const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
            const adminName = adminUser.name || 'Support Agent';

            const response = await fetch(API_ENDPOINTS.CUSTOMER_PORTAL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    action: 'send-message',
                    sessionId: selectedChat.sessionId,
                    sender: adminName,
                    message: inputText,
                    isAdmin: true
                })
            });

            console.log('Send message response status:', response.status);
            const data = await response.json();
            console.log('Send message response data:', data);

            if (!response.ok) {
                console.error('Failed to send message:', data);
                throw new Error(data.error || 'Failed to send message');
            }
            if (data.success) {
                setInputText('');
                loadMessages(selectedChat.sessionId); // Refresh immediately
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleCloseChat = async () => {
        if (!selectedChat || !window.confirm('Are you sure you want to close this chat?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            await fetch(API_ENDPOINTS.CUSTOMER_PORTAL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    action: 'close-chat',
                    sessionId: selectedChat.sessionId
                })
            });

            setSelectedChat(null);
            setMessages([]);
            loadActiveChats();
        } catch (error) {
            console.error('Error closing chat:', error);
        }
    };

    const formatTime = (isoString) => {
        try {
            return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch { return ''; }
    };

    return (
        <div className="flex h-[calc(100vh-140px)] bg-[#282828] rounded-xl border border-[#3c3836] overflow-hidden">
            {/* Sidebar - Chat List */}
            <div className="w-1/3 border-r border-[#3c3836] flex flex-col">
                <div className="p-4 border-b border-[#3c3836] bg-[#32302f]">
                    <h2 className="text-[#ebdbb2] font-semibold flex items-center">
                        <FaComments className="mr-2 text-[#fabd2f]" /> Active Chats ({chats.length})
                    </h2>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {chats.length === 0 ? (
                        <div className="p-8 text-center text-[#928374]">
                            <p>No active chats</p>
                        </div>
                    ) : (
                        chats.map(chat => (
                            <div
                                key={chat.sessionId}
                                onClick={() => setSelectedChat(chat)}
                                className={`p-4 border-b border-[#3c3836] cursor-pointer hover:bg-[#32302f] transition-colors ${selectedChat?.sessionId === chat.sessionId ? 'bg-[#32302f] border-l-4 border-l-[#fabd2f]' : ''}`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <span className="font-medium text-[#ebdbb2]">{chat.name}</span>
                                    <span className="text-xs text-[#928374]">{formatTime(chat.updatedAt)}</span>
                                </div>
                                <div className="text-sm text-[#bdae93] truncate">{chat.email}</div>
                                <div className="flex items-center mt-2">
                                    <span className="text-xs bg-[#b8bb26] text-[#1d2021] px-2 py-0.5 rounded-full font-bold">
                                        OPEN
                                    </span>
                                    {chat.phone && <span className="ml-2 text-xs text-[#928374]">{chat.phone}</span>}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col bg-[#1d2021]">
                {selectedChat ? (
                    <>
                        {/* Chat Header */}
                        <div className="p-4 border-b border-[#3c3836] flex justify-between items-center bg-[#32302f]">
                            <div className="flex items-center">
                                <div className="w-10 h-10 rounded-full bg-[#fabd2f] flex items-center justify-center text-[#1d2021] font-bold mr-3">
                                    {selectedChat.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="text-[#ebdbb2] font-semibold">{selectedChat.name}</h3>
                                    <p className="text-xs text-[#bdae93]">{selectedChat.email} • {selectedChat.accountNumber}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleCloseChat}
                                className="px-3 py-1.5 bg-[#cc241d] text-[#ebdbb2] rounded hover:bg-[#fb4934] text-sm transition-colors"
                            >
                                End Chat
                            </button>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {messages.map((msg, idx) => {
                                const isAdmin = msg.isAdmin || msg.sender === 'admin';
                                return (
                                    <div key={idx} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[70%] rounded-lg p-3 ${isAdmin
                                            ? 'bg-[#458588] text-[#ebdbb2] rounded-tr-none'
                                            : 'bg-[#3c3836] text-[#ebdbb2] rounded-tl-none'
                                            }`}>
                                            <div className="text-xs opacity-70 mb-1 flex justify-between gap-4">
                                                <span>{msg.sender}</span>
                                                <span>{formatTime(msg.timestamp)}</span>
                                            </div>
                                            <p className="whitespace-pre-wrap">{msg.message}</p>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <form onSubmit={handleSendMessage} className="p-4 border-t border-[#3c3836] bg-[#32302f]">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    placeholder="Type a message..."
                                    className="flex-1 bg-[#1d2021] border border-[#504945] rounded-lg px-4 py-2 text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]"
                                />
                                <button
                                    type="submit"
                                    disabled={sending || !inputText.trim()}
                                    className="bg-[#fabd2f] text-[#1d2021] p-3 rounded-lg hover:bg-[#fe8019] disabled:opacity-50 transition-colors"
                                >
                                    {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                                </button>
                            </div>
                        </form>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-[#928374]">
                        <FaComments size={48} className="mb-4 opacity-50" />
                        <p className="text-lg">Select a chat to start messaging</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LiveChat;
